import {escapeHTML} from "./lib/escapeHTML";
import {springErrorList, springHorizontalNumber, springNumber, springSelect, springText} from "./lib/springForms";
import {Messages} from "./messages/Messages";
import {BindingList, createHtmlElement, createHtmlElementFromHtml, querySelectorWithName, toggleHidden} from "./lib/domFunctions";
import {adaptErrors} from "./dto/org.springframework.validation";
import {ProcessInputModalSearch, ProcessInputModalSearchOptions} from "./modals/ProcessInputModalSearch";
import {fetchJson, fetchJsonForApiResponse, toastFetchError} from "./lib/fetch";
import {coalesce, coalesceSupply, firstNotBlank} from "./lib/coalesce";
import Sortable from 'sortablejs/modular/sortable.core.esm.js';

const messages = new Messages();

function removeFadeOut<T extends HTMLElement>(el: T, speed: number): Promise<T> {
	const seconds = speed / 1000;
	el.style.transition = "opacity " + seconds + "s ease";
	el.style.opacity = '0';
	return new Promise((resolve) => {
		setTimeout(function () {
			resolve(el)
			el.style.transition = "";
			el.style.opacity = '';
		}, speed);
	});
}

function toggleAttribute(el: Element | null, name: string, set: boolean) {
	if (el == null) return;
	if (set) el.setAttribute(name, `${set}`);
	else el.removeAttribute(name);
}

function flatMapToObject(entries: [string, any][]) {
	const partPattern = /^(.+)\[(\d+)]$/
	const result: any = {};
	for (const [key, value] of entries) {
		const parts = key.split('.');
		let current = result;
		for (let i = 0; i < parts.length; i++) {
			const part = parts[i];
			const match = part.match(partPattern);
			if (match) {
				const arrayName = match[1];
				const index = parseInt(match[2], 10);
				if (!current[arrayName]) current[arrayName] = [];
				if (i === parts.length - 1) {
					current[arrayName][index] = value;
				} else {
					if (!current[arrayName][index]) current[arrayName][index] = {};
					current = current[arrayName][index];
				}
			} else {
				if (i === parts.length - 1) {
					current[part] = value;
				} else {
					if (!current[part]) current[part] = {};
					current = current[part];
				}
			}
		}
	}
	return result;
}

const HAS_RECTIFIER_PROFILE = 'has-rectifier-profile';

interface ProcessEditPageParams {
	positionTypes: PositionTypeInfo[]
	rectifierProfiles: any
	instructionBlocks: InstructionBlockOption[],
	suspensionTakeDelayFactor: number,
	hasLiveContainer: boolean,
}

interface PositionTypeInfo {
	id: number,
	name: string,
	hasMaxCurrent: boolean,
	selectable: boolean
	putBlock: number | null
	takeBlock: number | null
}

type InstructionBlockParameterSpec = { name: string, label: string | null, value: number | null };
type InstructionBlockParameterNames = { [key: string]: InstructionBlockParameterSpec };

interface InstructionBlockOption {
	id: number
	name: string
	type: string
	parameters: InstructionBlockParameterNames
	targetPositionTypes: number[]
}

const ProcessFormActionKeys = new Set([
	'id', 'dynamic', 'refKey',
	'positionType', 'putInstructionBlock', 'takeInstructionBlock',
	'rectifierProfile', 'minCurrentDensity', 'maxCurrentDensity',
	'minDuration', 'maxDuration', 'runoffTime', 'aoSpeedDown', 'aoSpeedUp'
]);

interface ProcessFormAction {
	id: number | null
	dynamic: number | null
	refKey: string

	positionType: number | null
	putInstructionBlock: number | null
	takeInstructionBlock: number | null
	rectifierProfile: number | null
	minCurrentDensity: number | null
	maxCurrentDensity: number | null
	minDuration: number | null
	maxDuration: number | null
	runoffTime: number | null
	aoSpeedDown: number | null
	aoSpeedUp: number | null
}

interface RectifierProfileInfo {
	id: number,
	name: string,
	type: string
}

export class ProcessEditPage {
	private readonly positionTypes: PositionTypeInfo[];
	private readonly rectifierProfiles: RectifierProfileInfo[];
	private readonly instructionBlocks: InstructionBlockOption[];
	private readonly stepsTable: HTMLTableSectionElement;
	private readonly hasLiveContainer: boolean
	private stepCount = 0
	private processId: number | null = null

	constructor({positionTypes, rectifierProfiles, instructionBlocks, hasLiveContainer}: ProcessEditPageParams) {
		const processForm = this.processForm;
		if (!processForm) throw new Error('process form is not found on the page')

		this.hasLiveContainer = !!(hasLiveContainer as any);
		this.stepsTable = document.getElementById('steps')!.querySelector('tbody')!!
		this.positionTypes = positionTypes;
		this.rectifierProfiles = rectifierProfiles;
		this.instructionBlocks = instructionBlocks;

		processForm.addEventListener('submit', e => {
			e.preventDefault()
			this.submitSave();
		});

		processForm.addEventListener(`change`, e => {
			const target = e.target as HTMLSelectElement
			const tagName = coalesce(`${target && target.tagName}`, '');
			if (tagName == 'SELECT' && target.name.endsWith(`.putInstructionBlock`)) {
				this.refreshParameterBlockInputs(target.closest(`[data-group="single-action-form"]`))
			}
			if (tagName == 'SELECT' && target.name.endsWith(`.takeInstructionBlock`)) {
				this.refreshParameterBlockInputs(target.closest(`[data-group="single-action-form"]`))
			}
		});
	}

	start() {
		const processForm = this.processForm!!;

		Sortable.create(this.stepsTable, {
			handle: ".drag-handle",
			onStart: () => {
				this.stepsTable.closest('table')?.classList.remove('table-hover')
			},
			onEnd: () => {
				this.stepsTable.closest('table')?.classList.add('table-hover')
				this.recalculateOrders()
			}
		});

		this.stepsTable.addEventListener('change', e => {
			if (!e.target) return;

			const target = e.target as HTMLInputElement
			if (/\.positionType$/.test(target.name)) {
				this.updateRowStateOnPositionChange(target.closest('tr'))
			} else if (/\.rectifierProfile$|\.minDuration$/.test(target.name)) {
				this.updateRowState(target.closest('tr'))
			}
		});

		this.stepsTable.addEventListener('input', e => {
			if (!e.target) return;

			const target = e.target as HTMLInputElement
			if (/\.minDuration$/.test(target.name)) {
				this.updateRowState(target.closest('tr'))
			}
		});

		document.getElementById('processForm')
			?.querySelector('button[data-bind="checkGraph"]')
			?.addEventListener('click', () => this.submitCheck());

		if (this.hasLiveContainer) {
			const positionTypes = new Map<number, PositionTypeInfo>(this.positionTypes.map(e => [e.id, e]));
			const positionTypesLimited = new Set(this.positionTypes.filter(e => e.selectable && !e.hasMaxCurrent).map(e => e.id));

			const processFormBindings = new BindingList();
			processFormBindings.collect(processForm);

			processFormBindings.update<HTMLButtonElement>('addStep', node => node.remove());
			processFormBindings.update<HTMLButtonElement>('removeStep', node => node.remove());
			processFormBindings.update<HTMLButtonElement>('stepUp', node => node.remove());
			processFormBindings.update<HTMLButtonElement>('stepDown', node => node.remove());
			processFormBindings.update<HTMLButtonElement>('stepDrag', node => node.remove());

			function makeInert(el: HTMLElement) {
				el.setAttribute('readonly', 'readonly');
				el.inert = true;
				el.classList.add('form-control--readonly')
			}

			function makeReadonlySelect(select: HTMLSelectElement) {
				const currentValue = select.value;
				select.setAttribute('readonly', 'readonly');
				select.querySelectorAll('option').forEach(option => {
					if (option.value != currentValue) option.remove()
				})
				makeInert(select)
			}

			processFormBindings.update<HTMLFormElement>('containerType', el => makeInert(el));

			processFormBindings.update<HTMLTableRowElement>('stepRow', row => {
				row.querySelectorAll('input').forEach(input => {
					const sharedStepParameters = /\.(minDuration|maxDuration|runoffTime|aoSpeedDown|aoSpeedUp)$/.test(input.name);
					const customStepParameters = /steps\[\d+]\.params\./.test(input.name);
					if (!(sharedStepParameters || customStepParameters)) {
						makeInert(input)
					}
				});

				row.querySelectorAll<HTMLSelectElement>('select').forEach(select => {
					const positionTypeSelect = /\.positionType$/.test(select.name)
					if (positionTypeSelect) // Если выбор типа - то можно менять для не гальваники 
					{
						const currentType = positionTypes.get(parseInt(select.value));
						const allowTypeChange = !!currentType?.selectable && !currentType?.hasMaxCurrent;
						if (allowTypeChange) {
							select.querySelectorAll('option').forEach(option => {
								if (!positionTypesLimited.has(parseInt(option.value))) option.remove()
							})
						} else {
							makeReadonlySelect(select)
						}
					} else  // Если выбор другого параметра - все запретить!
					{
						makeReadonlySelect(select)
					}
				});
			});
		}

		processForm.removeAttribute('hidden')
	}

	private get processForm() {
		const form = document.getElementById('processForm') as HTMLFormElement;
		return !form ? null : form;
	}

	private updateRowState(tr: HTMLTableRowElement | null) {
		if (!tr) return
		this.updateRowShowMaxCurrentFields(tr)
		this.updateRowShowDynamicNotice(tr)
		this.updateRowShowDefaultBlockNames(tr);
	}

	private updateRowStateOnPositionChange(tr: HTMLTableRowElement | null) {
		if (!tr) return
		this.updateRowState(tr);
		this.updateRowFilterBlockOptions(tr);
	}

	private updateRowShowMaxCurrentFields(tr: HTMLTableRowElement) {
		function toggle(show: boolean) {
			toggleAttribute(tr, HAS_RECTIFIER_PROFILE, show);
			tr.querySelectorAll<HTMLElement>('[data-visible-for-max-current]').forEach(node => node.classList.toggle('hidden', !show));
		}

		const positionTypeSelect = querySelectorWithName<HTMLSelectElement>(tr, 'select', /\.positionType$/)
		if (!positionTypeSelect) return toggle(false);

		const positionType = Number(positionTypeSelect.value);
		const hasMaxCurrent = this.positionTypes.filter(t => t.id === positionType).map(t => t.hasMaxCurrent)[0] || false;

		toggle(hasMaxCurrent)
	}

	private updateRowShowDefaultBlockNames(tr: HTMLTableRowElement) {
		const positionTypeSelect = querySelectorWithName<HTMLSelectElement>(tr, 'select', /\.positionType$/)
		if (!positionTypeSelect) return;

		const positionType = this.positionTypes.find(it => it.id == Number.parseInt(positionTypeSelect.value));
		if (!positionType) return;

		const assignDefaultBlockNameFromPosition = (select: HTMLSelectElement, defaultLabelCode: string, blockId: any) => {
			const blockEntity = this.instructionBlocks.find(it => it.id == blockId)
			const selectDefault = select.querySelector(`option[value=""]`)
			if (selectDefault) {
				selectDefault.textContent = messages.get(defaultLabelCode) + (blockEntity ? ` ${blockEntity?.name}` : '')
				selectDefault.setAttribute("default-block-id", coalesce(blockEntity?.id, 0).toString())
			}
		}

		const putBlockSelect = querySelectorWithName<HTMLSelectElement>(tr, 'select', /\.putInstructionBlock$/)
		if (putBlockSelect && putBlockSelect.hasAttribute("defaultFromPosition")) {
			assignDefaultBlockNameFromPosition(putBlockSelect, 'processAction.form.putInstructionBlockDefault', positionType.putBlock)
		}
		const takeBlockSelect = querySelectorWithName<HTMLSelectElement>(tr, 'select', /\.takeInstructionBlock$/)
		if (takeBlockSelect && takeBlockSelect.hasAttribute("defaultFromPosition")) {
			assignDefaultBlockNameFromPosition(takeBlockSelect, 'processAction.form.takeInstructionBlockDefault', positionType.takeBlock)
		}

		tr.querySelectorAll<HTMLElement>(`[data-group="single-action-form"]`).forEach(group => {
			this.refreshParameterBlockInputs(group);
		})
	}

	private updateRowFilterBlockOptions(tr: HTMLTableRowElement) {
		const positionTypeSelect = querySelectorWithName<HTMLSelectElement>(tr, 'select', /\.positionType$/)
		if (!positionTypeSelect) return;

		const positionTypeIdText = positionTypeSelect.value;
		const positionTypeId = positionTypeIdText.length > 0 ? Number.parseInt(positionTypeIdText) : null;

		const putSelect = querySelectorWithName<HTMLSelectElement>(tr, 'select', /\.putInstructionBlock$/)
		if (putSelect) {
			putSelect.value = '';
			this.rebuildBlockSelect(putSelect, ['PUT'], positionTypeId);
		}

		const takeSelect = querySelectorWithName<HTMLSelectElement>(tr, 'select', /\.takeInstructionBlock$/)
		if (takeSelect) {
			takeSelect.value = '';
			this.rebuildBlockSelect(takeSelect, ['TAKE'], positionTypeId);
		}
	}

	private rebuildBlockSelect(select: HTMLSelectElement, allowedTypes: string[], positionTypeId: number | null) {
		const selectedValue = select.value;
		const defaultOption = select.options[0];
		const defaultBlockId = defaultOption ? Number.parseInt(defaultOption.getAttribute('default-block-id') || '0') : 0;
		const defaultLabel = defaultOption ? defaultOption.textContent || '' : '';

		while (select.options.length > 1) {
			select.remove(1);
		}

		this.instructionBlocks
			.filter(it => {
				if (positionTypeId == null) return false;
				if (!allowedTypes.includes(it.type) && it.id !== Number.parseInt(selectedValue)) return false;
				if (it.targetPositionTypes.length > 0 && !it.targetPositionTypes.includes(positionTypeId) && it.id !== Number.parseInt(selectedValue)) return false;
				return true;
			})
			.forEach(item => {
				const option = document.createElement('option');
				option.value = item.id.toString();
				option.textContent = item.name;
				if (item.id === Number.parseInt(selectedValue)) {
					option.selected = true;
				}
				select.append(option);
			});
	}

	private updateRowShowDynamicNotice(tr: HTMLTableRowElement) {
		function toggle(show: boolean) {
			tr.classList.toggle('dynamic-step-row', show)
			tr.querySelectorAll<HTMLElement>('[data-bind="dynamicNotice"]').forEach(node => toggleHidden(node, !show))
		}

		if (!tr.hasAttribute(HAS_RECTIFIER_PROFILE)) return toggle(false);

		const rectifierProfileSelect = querySelectorWithName<HTMLSelectElement>(tr, 'select', /\.rectifierProfile$/)
		if (!rectifierProfileSelect) return toggle(false);

		const rectifierProfile = Number(rectifierProfileSelect.value);
		const rectifierProfileTypeCurrent = this.rectifierProfiles.filter(t => t.id === rectifierProfile).map(t => t.type == 'CURRENT')[0] || false;

		const minDurationText = querySelectorWithName<HTMLInputElement>(tr, 'input', /\.minDuration$/)?.value
		const minDurationValue = parseFloat(minDurationText == undefined ? '' : minDurationText)

		toggle(rectifierProfileTypeCurrent && !Number.isFinite(minDurationValue))
	}

	addStep() {
		const stepIndex = this.stepCount++;
		const tr = this.renderPositionRow(stepIndex);

		this.updateRowState(tr);
		this.stepsTable.append(tr);
		this.recalculateOrders();

		tr.scrollIntoView(true);
	}

	addSubprocess() {
		const currentProcessId = this.processId || 0;
		const options: ProcessInputModalSearchOptions = {
			title: 'Поиск подпроцесса',
			search: (search, limit) => fetchJson(`/processes/listSubprocesses?search=${encodeURIComponent(search)}&exclude=${currentProcessId}&offset=0&limit=${limit}`)
		};
		new ProcessInputModalSearch((item) => {
			if (item == null || !item.id) return

			return fetchJsonForApiResponse(`/processes/buildProcessFormSubprocess?processId=${currentProcessId}&subprocessId=${item.id}`)
				.then(stepForm => {
					const stepIndex = this.stepCount++;
					const tr = this.renderSubprocessRow(stepIndex, stepForm);

					this.stepsTable.append(tr);
					this.recalculateOrders();

					tr.scrollIntoView(true);
					return Promise.resolve(true)
				})
				.catch(err => {
					toastFetchError(err);
					return Promise.resolve(false)
				});
		}, options);
	}

	private createSelectBlockModel(defaultLabelCode: string, allowedTypes: string[], positionBlock?: number | null, selectedBlock?: number | null, positionTypeId?: number | null) {
		const defaultEntity = this.instructionBlocks.find(it => it.id == positionBlock)
		const defaultLabel = messages.get(defaultLabelCode) + (defaultEntity ? ` ${defaultEntity?.name}` : '');
		const options = `<option value="" default-block-id="${defaultEntity?.id || 0}">${escapeHTML(defaultLabel)}</option>` + this.instructionBlocks
			.filter(it => {
				if (positionTypeId == null) return false;
				const isSelected = it.id == selectedBlock;
				const typeMatch = allowedTypes.includes(it.type) || isSelected;
				if (!typeMatch) return false;
				if (isSelected) return true;
				if (it.targetPositionTypes.length === 0) return true;
				return it.targetPositionTypes.includes(positionTypeId);
			})
			.map(item => `<option value="${item.id}" ${item.id == selectedBlock ? 'selected' : ''}>${escapeHTML(item.name)}</option>`).join('');
		return {options};
	}

	private findBlockParameters(...blockIdList: any[]) {
		// ищем первый доступный из списка
		const block = blockIdList
			.map(id => typeof id === "number" ? id : parseInt(id))
			.filter(id => Number.isFinite(id))
			.map(id => this.instructionBlocks.find(block => block.id == id))
			.filter(block => !!block)[0]
		if (!block || !block.parameters)
			return [];
		return Array.from(Object.values(block.parameters))
			.filter(parameter => parameter.name.length > 0);
	}

	private renderPositionRow(stepIndex: number, actionForm: any | null = null, formErrors: any = {}) {
		const blank = actionForm == null;
		if (actionForm == null) {
			actionForm = {}
		}

		formErrors = adaptErrors(formErrors)
		formErrors.changeMapKeysToPath(`steps[${stepIndex}].params`);

		const labelClass = `text-nowrap`;

		const positionType = coalesce(this.positionTypes.find(it => it.id == actionForm.positionType), null);
		const positionTypeOptions = (blank ? `<option value=""></option>` : '') + this.positionTypes
			.filter(item => item.selectable)
			.map(item => `<option value="${item.id}" ${item.id == actionForm.positionType ? 'selected' : ''} ${item.selectable ? '' : ' disabled '}>${escapeHTML(item.name)}</option>`)
			.join('');
		const positionTypeSelect = springSelect({name: `steps[${stepIndex}].positionType`, labelCode: 'processAction.form.positionType', optionContent: positionTypeOptions, required: true, formErrors});

		const rectifierProfileOptions = `<option value=""></option>` + this.rectifierProfiles
			.map(item => `<option value="${item.id}" ${item.id == actionForm.rectifierProfile ? 'selected' : ''}>${escapeHTML(item.name)}</option>`).join('');
		const rectifierProfileSelect = springSelect({name: `steps[${stepIndex}].rectifierProfile`, labelCode: 'processAction.form.rectifierProfile', optionContent: rectifierProfileOptions});

		const putBlockModel = this.createSelectBlockModel('processAction.form.putInstructionBlockDefault', ['PUT'], positionType?.putBlock, actionForm.putInstructionBlock, actionForm.positionType);
		const putBlockSelect = springSelect({name: `steps[${stepIndex}].putInstructionBlock`, labelCode: 'processAction.form.putInstructionBlock', optionContent: putBlockModel.options, formErrors, attributes: `data-watch="takeInstructionBlock" defaultFromPosition`})

		const takeBlockModel = this.createSelectBlockModel('processAction.form.takeInstructionBlockDefault', ['TAKE'], positionType?.takeBlock, actionForm.takeInstructionBlock, actionForm.positionType);
		const takeBlockSelect = springSelect({name: `steps[${stepIndex}].takeInstructionBlock`, labelCode: 'processAction.form.takeInstructionBlock', optionContent: takeBlockModel.options, formErrors, attributes: "defaultFromPosition"})

		const runoffTimeInput = springHorizontalNumber({labelWidth: 8, name: `steps[${stepIndex}].runoffTime`, labelCode: 'processAction.form.runoffTime', value: actionForm.runoffTime, min: 0, formErrors, labelClass});
		const aoSpeedDown = springHorizontalNumber({labelWidth: 8, name: `steps[${stepIndex}].aoSpeedDown`, labelCode: 'processAction.form.aoSpeedDown', value: actionForm.aoSpeedDown, min: 0, step: 1, formErrors, labelClass});
		const aoSpeedUp = springHorizontalNumber({labelWidth: 8, name: `steps[${stepIndex}].aoSpeedUp`, labelCode: 'processAction.form.aoSpeedUp', value: actionForm.aoSpeedUp, min: 0, step: 1, formErrors, labelClass});

		const blockParametersFieldPrefix = `steps[${stepIndex}].params`;
		const blockParametersGroup = this.createBlockParameterGroup(null, actionForm, actionForm.positionType, blockParametersFieldPrefix, formErrors)
			.map(e => e.html())
			.join('');

		const rowClass = `align-top ${actionForm.dynamic ? "dynamic-step-row" : ""}`
		const tr = createHtmlElement('tr', {class: rowClass, "data-bind": "stepRow"}, `
                            <td class="border-5 border-bottom border-top border-start px-3 pb-0">
                                <input type="hidden" name="steps[${stepIndex}].id" value="${actionForm.id || 0}">
                                <input type="hidden" name="steps[${stepIndex}].order" value="${actionForm.order || 0}" data-bind="order">
                                <input type="hidden" name="steps[${stepIndex}]._type" value="action">
                                <strong class="d-block text-center text-nowrap mb-2">
                                    ${messages.get('processAction.form.order')}
                                    <span data-bind="order">${actionForm.order}</span>
                                </strong>
                                <div class="text-center text-nowrap mb-3">
                                    <button type="button" class="btn btn-danger text-capitalize me-3" onclick="controller.removeStep(this)" title="${messages.get('process.form.stepRemove')}" data-bind="removeStep">
                                        <i class="fal fa-times"></i></button>
                                    <button type="button" class="btn btn-light drag-handle" title="${messages.get('process.form.stepDrag')}" data-bind="stepDrag">
                                        <i class="fal fa-sort-alt"></i></button>
                                </div>
                                
                                <div class="mt-2 text-lowercase text-center cursor-default hidden" data-bind="dynamicNotice" title="${messages.get('processAction.form.dynamicNotice')}">
                                    <small class="badge text-bg-info">${messages.get('processAction.form.dynamicNoticeShort')}</small>
                                </div>
                            </td>
                            <td class="border-5 border-bottom border-top border-end pb-0 pt-0" data-group="single-action-form">
								<div class="row">
                                    <div class="col-md-4 ">
                                    	<div class="pt-2 mb-3">
											<div class="mb-3 form-label-strong">${positionTypeSelect}</div>
											<div class="row mb-2">${springHorizontalNumber({labelWidth: 8, name: `steps[${stepIndex}].minDuration`, labelCode: 'processAction.form.minDuration', value: actionForm.minDuration, step: 1, formErrors, labelClass})}</div>
											<div class="row mb-2">${springHorizontalNumber({labelWidth: 8, name: `steps[${stepIndex}].maxDuration`, labelCode: 'processAction.form.maxDuration', value: actionForm.maxDuration, step: 1, formErrors, labelClass})}</div>
										</div>
                                    </div>
                                    <div class="col-md-4">
                                    	<div class="row pt-2">
											<div class="col-md-6 mb-3">${putBlockSelect}</div>
											<div class="col-md-6 mb-3">${takeBlockSelect}</div>
											<div class="col-md-12 mb-3" block-parameters-group="${blockParametersFieldPrefix}">
												<div class="row mb-2">${runoffTimeInput}</div>
												<div class="row mb-2">${aoSpeedDown}</div>
												<div class="row mb-2">${aoSpeedUp}</div>
												${blockParametersGroup}
											</div>
										</div>
									</div>
                                    <div class="col-md-4 rectifier-form-background" data-visible-for-max-current>
                                    	<div class="row pt-2">
											<div class="col-md-12 mb-3">${rectifierProfileSelect}</div>
											<div class="col-md-6 mb-3">${springNumber({name: `steps[${stepIndex}].minCurrentDensity`, labelCode: 'processAction.form.minCurrentDensity', value: actionForm.minCurrentDensity, step: 'any', formErrors, labelClass})}</div>
											<div class="col-md-6 mb-3">${springNumber({name: `steps[${stepIndex}].maxCurrentDensity`, labelCode: 'processAction.form.maxCurrentDensity', value: actionForm.maxCurrentDensity, step: 'any', formErrors, labelClass})}</div>
										</div>
									</div>
                                </div>                                
                            </td>
         `)

		return tr;
	}

	renderSubprocessRow(stepIndex: number, actionForm: any | null = null, formErrors: any = {}) {
		if (actionForm == null) actionForm = {}

		formErrors = adaptErrors(formErrors)
		formErrors.changeMapKeysToPath(`steps[${stepIndex}].overrides`);

		let subprocessIdErrors = formErrors.getFieldErrors(`steps[${stepIndex}].subprocessId`)
		let subprocessIdErrorList = springErrorList(subprocessIdErrors)
		if (subprocessIdErrorList.length > 0) {
			// тут нам нужно поле добавить, иначе список ошибки будет скрыт CSS классом
			subprocessIdErrorList = `<input type="hidden" class="is-invalid"/>` + subprocessIdErrorList;
		}

		let actionsList = (actionForm.steps || []) as ProcessFormAction[]
		let overridesMaps = (actionForm.overrides || {}) as any
		let actionsHtml = ``;

		for (let i = 0; i < actionsList.length; ++i) {
			const inner = actionsList[i];
			const innerPositionType = coalesce(this.positionTypes.find(t => t.id === inner.positionType), null);
			const innerRectifierProfile = coalesce(this.rectifierProfiles.find(t => t.id === inner.rectifierProfile), null);
			const innerOverride = overridesMaps[inner.refKey] || {};

			const labelClass = `text-nowrap`;
			const overrideNamePrefix = `steps[${stepIndex}].overrides.${inner.refKey}`;

			const positionTypeSelect = springText({labelCode: 'processAction.form.positionType', value: innerPositionType?.name, readonly: true, inert: true, formFieldErrors: formErrors.getFieldErrors(`${overrideNamePrefix}.positionType`)});
			const runoffTimeInput = springHorizontalNumber({labelWidth: 8, name: `${overrideNamePrefix}.runoffTime`, labelCode: 'processAction.form.runoffTime', value: innerOverride.runoffTime, placeholder: inner.runoffTime, min: 0, formErrors, labelClass});
			const aoSpeedDown = springHorizontalNumber({labelWidth: 8, name: `${overrideNamePrefix}.aoSpeedDown`, labelCode: 'processAction.form.aoSpeedDown', value: innerOverride.aoSpeedDown, placeholder: inner.aoSpeedDown, min: 0, step: 1, formErrors, labelClass});
			const aoSpeedUp = springHorizontalNumber({labelWidth: 8, name: `${overrideNamePrefix}.aoSpeedUp`, labelCode: 'processAction.form.aoSpeedUp', value: innerOverride.aoSpeedUp, placeholder: inner.aoSpeedUp, min: 0, step: 1, formErrors, labelClass});

			const putBlockModel = this.createSelectBlockModel('processAction.form.putInstructionBlockDefault', ['PUT'], inner.putInstructionBlock || innerPositionType?.putBlock, innerOverride.putInstructionBlock, inner.positionType);
			const putBlockSelect = springSelect({name: `${overrideNamePrefix}.putInstructionBlock`, labelCode: 'processAction.form.putInstructionBlock', optionContent: putBlockModel.options, attributes: `data-watch="takeInstructionBlock"`, formFieldErrors: formErrors.getFieldErrors(`${overrideNamePrefix}.putInstructionBlock`)})

			const takeBlockModel = this.createSelectBlockModel('processAction.form.takeInstructionBlockDefault', ['TAKE'], inner.takeInstructionBlock || innerPositionType?.takeBlock, innerOverride.takeInstructionBlock, inner.positionType);
			const takeBlockSelect = springSelect({name: `${overrideNamePrefix}.takeInstructionBlock`, labelCode: 'processAction.form.takeInstructionBlock', optionContent: takeBlockModel.options, formFieldErrors: formErrors.getFieldErrors(`${overrideNamePrefix}.takeInstructionBlock`)})

			let stepRectifierValuesSection = '';
			if (innerPositionType && innerPositionType.hasMaxCurrent) {
				stepRectifierValuesSection = `
						<div class="col-md-4 rectifier-form-cell">
							<div class="row pt-2">
								<div class="col-md-12 mb-3">${springText({labelCode: 'processAction.form.rectifierProfile', value: innerRectifierProfile?.name, readonly: true, inert: true})}</div>
								<div class="col-md-6 mb-3">${springNumber({name: `${overrideNamePrefix}.minCurrentDensity`, labelCode: 'processAction.form.minCurrentDensity', value: innerOverride.minCurrentDensity, placeholder: inner.maxCurrentDensity, step: 'any', formErrors, labelClass})}</div>
								<div class="col-md-6 mb-3">${springNumber({name: `${overrideNamePrefix}.maxCurrentDensity`, labelCode: 'processAction.form.maxCurrentDensity', value: innerOverride.maxCurrentDensity, placeholder: inner.maxCurrentDensity, step: 'any', formErrors, labelClass})}</div>
							</div>
						</div>
			`;
			}

			const blockParametersGroup = this.createBlockParameterGroup(innerOverride, inner, inner.positionType, overrideNamePrefix, formErrors)
				.map(e => e.html())
				.join('');

			actionsHtml += `<div class="border-1 border-start ms-n2 ps-2 me-n2 pe-2 border-top pb-0 pt-0 rectifier-form-border-color substep-action" data-group="single-action-form">
								<div class="row">
                                    <div class="col-md-4 ">
                                    	<div class="pt-2 mb-3">
											<div class="mb-3 form-label-strong">
												<input type="hidden" readonly disabled name="${overrideNamePrefix}.positionType" value="${inner.positionType}"/>
												${positionTypeSelect}
											</div>
											<div class="row mb-2">${springHorizontalNumber({labelWidth: 8, name: `${overrideNamePrefix}.minDuration`, labelCode: 'processAction.form.minDuration', value: innerOverride.minDuration, placeholder: inner.minDuration, step: 1, formErrors, labelClass})}</div>
											<div class="row mb-2">${springHorizontalNumber({labelWidth: 8, name: `${overrideNamePrefix}.maxDuration`, labelCode: 'processAction.form.maxDuration', value: innerOverride.maxDuration, placeholder: inner.maxDuration, step: 1, formErrors, labelClass})}</div>
										</div>
                                    </div>
                                    <div class="col-md-4">
                                    	<div class="row pt-2">
											<div class="col-md-6 mb-3">${putBlockSelect}</div>
											<div class="col-md-6 mb-3">${takeBlockSelect}</div>
											<div class="col-md-12 mb-3" block-parameters-group="${overrideNamePrefix}">
												<div class="row mb-2">${runoffTimeInput}</div>
												<div class="row mb-2">${aoSpeedDown}</div>
												<div class="row mb-2">${aoSpeedUp}</div>
												${blockParametersGroup}
											</div>
										</div>
									</div>
									${stepRectifierValuesSection}
                                </div>                   
                            </div>
         `;
		}

		const rowClass = `align-baseline`
		const tr = createHtmlElement('tr', {class: rowClass, "data-bind": "stepRow"}, `
                            <td class="border-5 border-bottom border-top border-start px-3 pb-0">
                                <input type="hidden" name="steps[${stepIndex}].id" value="${actionForm.id || 0}">
                                <input type="hidden" name="steps[${stepIndex}].order" value="${actionForm.order || 0}" data-bind="order">
                                <input type="hidden" name="steps[${stepIndex}]._type" value="subprocess">
                                <input type="hidden" name="steps[${stepIndex}].subprocessId" value="${actionForm.subprocessId}">
                                <strong class="d-block text-center text-nowrap mb-2">
                                    <span>${messages.get('processAction.form.order')}</span>
                                    <span data-bind="order">${actionForm.order}</span>
                                </strong>
                                <div class="text-center text-nowrap mb-3">
                                    <button type="button" class="btn btn-danger text-capitalize me-3" onclick="controller.removeStep(this)" title="${messages.get('process.form.stepRemove')}" data-bind="removeStep">
                                        <i class="fal fa-times"></i></button>
                                    <button type="button" class="btn btn-light drag-handle" title="${messages.get('process.form.stepDrag')}" data-bind="stepDrag">
                                        <i class="fal fa-sort-alt"></i></button>
                                </div>
                            </td>
                            <td class="border-5 border-bottom border-top border-end pb-0">
                            	<div class="mb-3 form-label-strong ">
                            		<label class="form-label "><span class="text-muted fw-normal">Подпроцесс: </span><span class="text-success">${escapeHTML(actionForm.subprocessEntity.name)}</span></label>
                            		${subprocessIdErrorList}
                            	</div>
                            	${actionsHtml}
                            </td>
         `)

		return tr;
	}

	private createBlockParameterGroup(overrideSubStepParams: any | null, originalActionForm: any, positionTypeId: number | null, paramFieldNamePrefix: string | null, formErrors: any) {
		formErrors = adaptErrors(formErrors);

		function toNumber(o: any): number | null {
			if (typeof o === "string") o = Number.parseInt(o);
			if (typeof o === "number") return o;
			return null;
		}

		function toEmptyString(o: string | number | null): string {
			if (typeof o === 'string') return o;
			if (typeof o === 'number') return `${o}`;
			return '';
		}

		const inSubStep = overrideSubStepParams != null;
		overrideSubStepParams = coalesce(overrideSubStepParams, {})
		originalActionForm = coalesce(originalActionForm, {})
		const originalActionParams = coalesce(originalActionForm.params, {});

		const positionType = this.positionTypes.find(it => it.id == positionTypeId);
		// console.info(`put block ${[overrideSubStepParams.putInstructionBlock, originalActionForm.putInstructionBlock, positionType?.putBlock]}. take block ${[overrideSubStepParams.takeInstructionBlock, originalActionForm.takeInstructionBlock, positionType?.takeBlock]}`)

		const blockParameters = new Map<string, InstructionBlockParameterSpec>();
		this.findBlockParameters(overrideSubStepParams.putInstructionBlock, originalActionForm.putInstructionBlock, positionType?.putBlock)
			.concat(this.findBlockParameters(overrideSubStepParams.takeInstructionBlock, originalActionForm.takeInstructionBlock, positionType?.takeBlock))
			.forEach(parameter => blockParameters.set(parameter.name, parameter));

		const allNames = new Set<string>();
		Array.from(blockParameters.keys()).forEach(n => allNames.add(n));
		Object.keys(overrideSubStepParams).forEach(n => allNames.add(n));
		Object.keys(originalActionParams).forEach(n => allNames.add(n));
		// console.info(Array.from(allNames))

		return Array.from(allNames)
			.filter(name => !ProcessFormActionKeys.has(name))
			.map(name => {
				const spec = blockParameters.get(name) || {name, value: null, label: ''};
				const value = inSubStep ? overrideSubStepParams[name] : originalActionParams[name];
				const placeholderSuper = toEmptyString(inSubStep ? overrideSubStepParams[name] : null);
				const placeholderBlock = toEmptyString(spec.value);
				const placeholder = placeholderSuper.length > 0 ? placeholderSuper : placeholderBlock;

				const disabled = !blockParameters.has(name);
				const hiddenAttr = disabled ? ` hidden` : ``;
				const label = coalesce(spec.label, spec.name);
				const formFieldName = `${paramFieldNamePrefix}.${spec.name}`;
				const formFieldErrors = formErrors.getFieldErrors(formFieldName);
				const labelClass = `text-nowrap`;
				return {
					name: spec.name,
					disabled,
					label,
					placeholderBlock,
					html: () => `<div class="row mb-2" block-parameter="${spec.name}" ${hiddenAttr} placeholder-super="${placeholderSuper}" placeholder-block="${placeholderBlock}">${springHorizontalNumber({labelWidth: 8, name: formFieldName, label, placeholder, value, formFieldErrors, labelClass})}</div>`
				}
			})
			.sort((a, b) => coalesce(a.label, '').localeCompare(coalesce(b.label, '')));
	}

	private refreshParameterBlockInputs(group: HTMLElement | null) {
		if (!group) return;

		const currentGroup = group.querySelector("[block-parameters-group]")
		if (!currentGroup) return;

		const fieldNamePrefix = currentGroup.getAttribute("block-parameters-group");

		const parameterElements = new Map<string, HTMLElement>();
		currentGroup.querySelectorAll<HTMLElement>("[block-parameter]").forEach(parameterEl => {
			const blockParameter = parameterEl.getAttribute("block-parameter");
			if (blockParameter) {
				parameterElements.set(blockParameter, parameterEl);
			}
		});

		const positionTypeId = Number.parseInt((group.querySelector(`[name$=".positionType"]`) as HTMLInputElement).value);
		const positionType = this.positionTypes.find(it => it.id == positionTypeId);

		const putBlockOption = findSelectedOption(group.querySelectorAll<HTMLOptionElement>(`[name$=".putInstructionBlock"] option`));
		const putBlockId = !putBlockOption ? null : Number.parseInt(putBlockOption.value.length > 0 ? putBlockOption.value : putBlockOption.getAttribute('default-block-id')!!);

		const takeBlockOption = findSelectedOption(group.querySelectorAll<HTMLOptionElement>(`[name$=".takeInstructionBlock"] option`));
		const takeBlockId = !takeBlockOption ? null : Number.parseInt(takeBlockOption.value.length > 0 ? takeBlockOption.value : takeBlockOption.getAttribute('default-block-id')!!);

		const localOverride = {} as any;
		if (putBlockId != null) localOverride.putInstructionBlock = putBlockId;
		if (takeBlockId != null) localOverride.takeInstructionBlock = takeBlockId;

		const parameterList = this.createBlockParameterGroup(localOverride, {}, positionTypeId, fieldNamePrefix, null);
		// console.info(parameterList);

		parameterList.forEach(p => {
			let pElement = coalesceSupply(parameterElements.get(p.name), () => {
				const el = createHtmlElementFromHtml(p.html());
				currentGroup.append(el);
				return el;
			});
			parameterElements.delete(p.name);

			pElement.hidden = p.disabled;
			pElement.setAttribute('placeholder-block', firstNotBlank(p.placeholderBlock));

			const pInput = pElement.querySelector(`input`)!!;
			pInput.disabled = p.disabled;
			pInput.placeholder = firstNotBlank(pElement.getAttribute('placeholder-super'), pElement.getAttribute('placeholder-block'));

			const pLabel = pElement.querySelector(`label`)!!;
			pLabel.textContent = p.label;
		})

		// Теперь всем остальным (не использующимся) надо сделать так, чтобы они не отправлялись на сервер (inert)
		Array.from(parameterElements.values()).forEach(pElement => {
			pElement.hidden = true;

			const pInput = pElement.querySelector(`input`)!!;
			pInput.disabled = true;
		})

		function findSelectedOption(options: NodeListOf<HTMLOptionElement>) {
			return Array.from(options).find(option => option.selected) || null;
		}
	}

	displayForm(processId: number | null, form: any, formErrors: any) {
		console.info(form);
		console.info(formErrors);
		this.processId = processId;

		for (const stepForm of form.steps) {
			const stepIndex = this.stepCount++;

			let tr = null;
			if (stepForm._type == 'action') {
				tr = this.renderPositionRow(stepIndex, stepForm, formErrors);
			}
			if (stepForm._type == 'subprocess') {
				tr = this.renderSubprocessRow(stepIndex, stepForm, formErrors);
			}

			if (tr == null) {
				console.error(`cannot render row ${stepForm.id}`)
				console.error(tr)
				continue
			}

			this.updateRowState(tr);
			this.stepsTable.append(tr);
		}

		this.start()
	}

	private recalculateOrders() {
		this.stepsTable.querySelectorAll('tr').forEach((tr, index) => {
			const orderString = (index + 1).toString();
			tr.querySelectorAll<HTMLInputElement>('input[data-bind="order"]').forEach(input => input.value = orderString)
			tr.querySelectorAll<HTMLElement>('span[data-bind="order"]').forEach(node => node.innerText = orderString)
		});
	}

	removeStep(button: HTMLButtonElement) {
		removeFadeOut(button.closest('tr')!!, 300).then(tr => {
			tr.remove();
			this.recalculateOrders();
		});
	}

	stepOrderMinus(button: HTMLButtonElement) {
		const tr = button.closest('tr')!!
		const previous = tr.previousElementSibling
		if (previous) {
			swapWithNext(previous).then(() => this.recalculateOrders());
		}
	}

	stepOrderPlus(button: HTMLButtonElement) {
		const tr = button.closest('tr')!!
		const next = tr.nextElementSibling
		if (next) {
			swapWithNext(tr).then(() => this.recalculateOrders());
		}
	}

	private createSubmitForm(): HTMLFormElement | null {
		const original = this.processForm;
		if (!original) {
			return null;
		}

		const originalData = new FormData(original)
		const formJson = flatMapToObject(Array.from(originalData.entries()))
		delete formJson['_csrf'];

		console.info(formJson)

		const form = createHtmlElement(`form`, {});
		form.method = original.method
		form.action = original.action
		form.enctype = original.enctype
		form.hidden = true
		document.body.append(form)

		form.append(createHtmlElement(`input`, {type: "hidden", name: '_csrf', value: original.querySelector<HTMLInputElement>(`input[name="_csrf"]`)!!.value}))
		form.append(createHtmlElement(`input`, {type: "hidden", name: 'body', value: JSON.stringify(formJson)}))

		return form;
	}

	private submitSave() {
		const form = this.createSubmitForm()
		if (!form) return

		form.submit()
	}

	private submitCheck() {
		const form = this.createSubmitForm()
		if (!form) return

		form.append(createHtmlElement('input', {type: 'hidden', name: 'check', value: 'check'}));
		form.submit()
	}
}

function swapWithNext(el: Element): Promise<boolean> {
	const next = el.nextElementSibling;
	if (!next) return Promise.reject('no next sibling');

	let rowHeight = el.clientHeight;
	let animationLength = 300;

	el.setAttribute('style', `transform: translate(0px, ${rowHeight}px); background-color:  cadetblue; transition: transform ${animationLength}ms`);
	next.setAttribute('style', `transform: translate(0px, -${rowHeight}px); background-color: aliceblue; transition: transform ${animationLength}ms`);

	return new Promise((resolve) => {
		setTimeout(function () {
			next.after(el);
			next.removeAttribute('style');
			el.removeAttribute('style');

			resolve(true);
		}, animationLength);
	})
}
